const STATE_NAME = {
  backToMerchant: 'back_to_merchant',
  billing: 'billing',
  donePage: 'DONE_PAGE',
  fallbackHermes: 'redirect_hermes',
  fallbackXoon: 'redirect_xoon',
  init: 'XO_SIGNUP_LITE_INIT',
  signup: 'signup',
};

const TRANSITION_NAME = {
  backToMerchant: 'prepare_back_to_merchant',
  cancelAndRtm: 'prepare_cancel_and_back_to_merchant',
  fallbackHermes: 'process_redirect_hermes',
  fallbackXoon: 'process_redirect_xoon',
  interactive: 'prepare_billing_page_interactive',
  isAddressless: 'prepare_address_fields',
  login: 'prepare_login',
  pageLoaded: 'prepare_billing_page_loaded',
  prepareBilling: 'prepare_billing_page',
  render: 'prepare_billing',
  renderThreeDS: 'prepare_threeDS_page',
  submitPayment: 'process_billing',
};

type COMMON_PAYLOAD_TYPE = {
  buyer_type: string;
  comp: string;
  context_id: string | null;
  context_type: string;
  /** Customer/Buyer ID from auth context. Null for guest users. */
  cust_id: string | null;
  customer_type: string | null;
  design: string;
  enrich: string;
  experience: string;
  feed_name: string;
  host_name: string;
  /** Merchant/Seller ID from checkout session. Null if not yet loaded. */
  seller_id: string | null;
  serverside_data_source: string;
  space_key: string;
  'x-paypal-fpti':
    | {
        context_type: string;
      }
    | string;
  integration_type?: string;
  token?: string;
};
// Logged for every event, is merged with payload param in fpti function
const COMMON_PAYLOAD: COMMON_PAYLOAD_TYPE = {
  buyer_type: 'NewUser',
  comp: 'checkoutuinodeweb',
  context_id: null,
  context_type: 'EC-TOKEN',
  cust_id: null,
  customer_type: null,
  design: 'full context',
  enrich: 'n',
  experience: 'signuplite',
  feed_name: 'checkoutuinodeweb',
  host_name: window.location.hostname,
  seller_id: null,
  serverside_data_source: 'checkout',
  space_key: 'SKS09G',
  'x-paypal-fpti': 'TBD',
};

const ACCOUNT_CREATION_PAYLOAD = (data: {
  buyerId: string;
  cartAmount: { currencyCode: string; currencyValue: number };
  locale: { country: string };
  merchantId: string;
}): {
  amt: number;
  ccpg: string;
  comp: string;
  curr: string;
  cust: string;
  mrid: string;
  page: string;
  pgrp: string;
} => {
  const { buyerId, cartAmount, locale, merchantId } = data;
  return {
    amt: cartAmount.currencyValue,
    ccpg: locale.country,
    comp: 'checkoutuinodeweb',
    curr: cartAmount.currencyCode,
    cust: buyerId,
    mrid: merchantId,
    page: 'main:xo:lite:weasley:guestAndSignup:billing',
    pgrp: 'main:xo:lite',
  };
};

const INPUT_FIELD_ERROR_STATUS_PAYLOAD = (
  fieldName: string,
  isValid: boolean,
  errorCode: string,
): {
  int_error_code: string;
  int_error_desc: any;
  state_name: string;
  transition_name: string;
} => ({
  int_error_code: isValid ? '0' : '1',
  int_error_desc: isValid ? '' : errorCode,
  state_name: STATE_NAME.billing,
  transition_name: `process_${fieldName}_field_error_status`,
});

const ERROR_PAYLOAD = ({
  code,
  desc,
  source,
}: {
  code: string;
  desc: string;
  source: string;
}): {
  error_source: string;
  int_error_code: string;
  int_error_desc: string;
} => ({
  error_source: source,
  int_error_code: code,
  int_error_desc: desc,
});

const INTERSTITIAL_PAYLOAD = ({
  action,
  name,
  stateName,
}: {
  action: string;
  name: string;
  stateName?: string;
}): { state_name: string; transition_name: string } => ({
  state_name: stateName || STATE_NAME.signup,
  transition_name: `prepare_${name}_interstitial_${action}`,
});

const IS_ADDRESSLESS_PAYLOAD = (
  isAddressless: boolean,
): { is_addr_shown: number; state_name: string; transition_name: string } => ({
  is_addr_shown: isAddressless ? 0 : 1,
  state_name: STATE_NAME.signup,
  transition_name: TRANSITION_NAME.isAddressless,
});

const DEFAULT_TAB_FIELD_INDEX = (
  tabName: string,
): {
  default_tab_index: string;
  state_name: string;
  transition_name: string;
} => ({
  default_tab_index: tabName,
  state_name: STATE_NAME.signup,
  transition_name: `prepare_${tabName}_tab_on_load`,
});

const LOG_IN_PAYLOAD = (): { state_name: string; transition_name: string } => ({
  state_name: STATE_NAME.signup,
  transition_name: TRANSITION_NAME.login,
});

const ONBOARD_OPTION_CHANGED_PAYLOAD = (
  accountType: string,
): {
  account_signup_type: string;
  state_name: string;
  transition_name: string;
} => ({
  account_signup_type: accountType,
  state_name: 'billing',
  transition_name: 'process_account_type_selection',
});

const ONBOARD_SUCCESS_PAYLOAD = ({
  mode,
  startTime,
}: {
  mode: keyof typeof STATE_NAME;
  startTime: number;
}): { event_time: number; state_name: string; transition_name: string } => ({
  event_time: Date.now() - startTime,
  state_name: STATE_NAME[mode],
  transition_name: `process_${mode}_checkout_success`,
});

const PAGE_LOAD_PAYLOAD = (
  isLoaded = false,
): { page_load_time: number; state_name: string; transition_name: string } => {
  const transition = isLoaded ? 'pageLoaded' : 'interactive';

  return {
    page_load_time: window.performance?.now(),
    state_name: STATE_NAME.billing,
    transition_name: TRANSITION_NAME[transition],
  };
};

const PREPARE_BILLING_PAYLOAD = ({
  hasPayLaterIntent,
  isOptionalSignup,
  isTabView,
}: {
  hasPayLaterIntent: boolean;
  isOptionalSignup: boolean;
  isTabView: boolean;
}): {
  entry_point: string;
  event_name: string;
  forced_signup_offered: number;
  pay_upon_invoice: string;
  ship_to_billing: string;
  state_name: string;
  transition_name: string;
  view_name: string;
} => ({
  entry_point: hasPayLaterIntent ? 'pay_later' : 'pay_now',
  event_name: 'xolite_prepare_billing_page',
  forced_signup_offered: isOptionalSignup ? 0 : 1,
  pay_upon_invoice: 'N',
  ship_to_billing: 'Y',
  state_name: STATE_NAME.billing,
  transition_name: TRANSITION_NAME.prepareBilling,
  view_name: isTabView ? 'tab_view' : 'single_page_view',
});

const SIGNUP_PAYLOAD = ({
  action,
}: { action?: keyof typeof TRANSITION_NAME } = {}): {
  state_name: string;
  transition_name: string;
} => ({
  state_name: STATE_NAME.signup,
  // @ts-expect-error this worked pre typescript
  transition_name: TRANSITION_NAME[action],
});

const TRANSACTION_DETAILS_PAYLOAD = (checkoutSession: {
  cart: { amounts: any };
  merchant: { merchantId: string };
}): { amt: number; currency_code: string; seller_id: string } => {
  const {
    cart: { amounts },
    merchant: { merchantId },
  } = checkoutSession;
  const { currencyCode = '', currencyValue = 0 } = amounts?.total || {};

  return {
    // Amount in cents
    amt: parseFloat(currencyValue || 0) * 100,
    currency_code: currencyCode || '',
    seller_id: merchantId,
  };
};

const TRACK_FIELD_INPUT_PAYLOAD = ({
  charCount,
  fieldName,
  fieldType,
}: {
  charCount: number;
  fieldName: string;
  fieldType: string;
}): {
  char_cnt: number;
  field_name: string;
  field_type: string;
  state_name: string;
  transition_name: string;
} => ({
  char_cnt: charCount,
  field_name: fieldName,
  field_type: fieldType,
  state_name: 'form_fill',
  transition_name: 'field_fill',
});

export {
  ACCOUNT_CREATION_PAYLOAD,
  COMMON_PAYLOAD,
  ERROR_PAYLOAD,
  INPUT_FIELD_ERROR_STATUS_PAYLOAD,
  INTERSTITIAL_PAYLOAD,
  IS_ADDRESSLESS_PAYLOAD,
  DEFAULT_TAB_FIELD_INDEX,
  LOG_IN_PAYLOAD,
  ONBOARD_OPTION_CHANGED_PAYLOAD,
  ONBOARD_SUCCESS_PAYLOAD,
  PAGE_LOAD_PAYLOAD,
  PREPARE_BILLING_PAYLOAD,
  SIGNUP_PAYLOAD,
  STATE_NAME,
  TRACK_FIELD_INPUT_PAYLOAD,
  TRANSACTION_DETAILS_PAYLOAD,
  TRANSITION_NAME,
};
